import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";

const TOC = [
  ["collect", "What information do we collect?"],
  ["process", "How do we process your information?"],
  ["share", "When and with whom do we share your personal information?"],
  ["ai", "Do we offer artificial intelligence-based products?"],
  ["retention", "How long do we keep your information?"],
  ["safe", "How do we keep your information safe?"],
  ["minors", "Do we collect information from minors?"],
  ["rights", "What are your privacy rights?"],
  ["dnt", "Controls for Do-Not-Track features"],
  ["australia", "What privacy laws apply in Australia?"],
  ["updates", "Do we make updates to this Privacy Policy?"],
  ["contact", "How can you contact us about this Privacy Policy?"],
  ["review", "How can you review, update, or delete the data we collect from you?"],
];

const Section = ({ id, num, title, children }) => (
  <section id={id} data-testid={`privacy-section-${id}`} className="scroll-mt-28 border-t border-neutral-200 pt-12 mt-12">
    <h2 className="font-display text-xl font-bold tracking-tight lg:text-2xl">
      <span className="mr-3 text-sm font-medium text-neutral-500">{String(num).padStart(2, "0")}</span>
      {title}
    </h2>
    <div className="mt-6 space-y-4 text-base leading-relaxed text-neutral-600">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc space-y-2 pl-5">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

const Sub = ({ children }) => (
  <h3 className="pt-2 font-display text-lg font-bold tracking-tight text-neutral-950">{children}</h3>
);

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="pt-40 pb-32 lg:pt-48">
      <Seo
        title="Privacy Policy — DISJINX"
        description="How DISJINX collects, uses, shares and protects your personal information."
        path="/privacy"
      />
      <div className="mx-auto max-w-3xl px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Legal</p>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight lg:text-5xl">Privacy Policy.</h1>
          <p className="mt-4 text-sm font-medium text-neutral-500">Last Updated: August 27, 2026</p>

          <div className="mt-10 space-y-4 text-base leading-relaxed text-neutral-600">
            <p>
              This Privacy Policy for DISJINX ('we', 'us', or 'our'), describes how and why we might access, collect,
              store, use and share ('process') your personal information when you use our services ('Services'), including when you:
            </p>
            <Bullets
              items={[
                "Visit our website at disjinx.com or any website of ours that links to this Privacy Policy",
                "Use our AI Automation Services.",
                "Engage with us in other related ways, including any marketing or events.",
              ]}
            />
            <p>
              <strong className="font-semibold text-neutral-950">Questions or concerns?</strong> Reading this Privacy Policy
              will help you understand your privacy rights and choices. We are responsible for making decisions about how your
              personal information is processed. If you do not agree with our policies and practices, please do not use our
              Services. If you still have any questions or concerns, please contact us at disjinx.support@gmail.com.
            </p>
          </div>

          <div className="mt-12 border border-neutral-200 bg-neutral-50 p-8">
            <h2 className="font-display text-lg font-bold tracking-tight">Summary of Key Points</h2>
            <p className="mt-2 text-sm text-neutral-500">
              This summary provides key points from our Privacy Policy, but you can find out more details about any of these
              topics by using our table of contents below.
            </p>
            <div className="mt-6 space-y-5 text-sm leading-relaxed text-neutral-600">
              <p><strong className="font-semibold text-neutral-950">What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the features you use.</p>
              <p><strong className="font-semibold text-neutral-950">Do we process any sensitive personal information?</strong> We do not process sensitive personal information. Some of the information which may be considered 'special' or 'sensitive' in certain jurisdictions includes: your racial or ethnic origins, sexual orientation, and religious beliefs, which again, we do not collect.</p>
              <p><strong className="font-semibold text-neutral-950">How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud protection, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so.</p>
              <p><strong className="font-semibold text-neutral-950">In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties. Refer to "when and with whom we share your personal information" to learn more.</p>
              <p><strong className="font-semibold text-neutral-950">How do we keep your information safe?</strong> We have adequate organisational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Refer to "how we keep your information safe" to learn more.</p>
              <p><strong className="font-semibold text-neutral-950">How do you exercise your rights?</strong> The easiest way to exercise your rights is by visiting https://disjinx.com/account, or by contacting us through https://disjinx.com/contact or by emailing us at disjinx.support@gmail.com. We will consider and act upon any request in accordance with applicable data and protection laws.</p>
            </div>
          </div>

          <nav data-testid="privacy-toc" className="mt-12">
            <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Table of contents</h2>
            <ol className="mt-5 list-decimal space-y-2 pl-5 text-sm font-medium text-neutral-950">
              {TOC.map(([id, label], i) => (
                <li key={id}>
                  <a href={`#${id}`} className="underline-offset-2 transition-colors duration-200 hover:text-neutral-500 hover:underline">
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <Section id="collect" num={1} title="What information do we collect?">
            <Sub>Personal information you disclose to us</Sub>
            <p>
              We collect personal information that you voluntarily provide to us when you register on the Services, express
              an interest in obtaining information about us or our products and Services, when you participate in activities
              on the Services, or otherwise when you contact us.
            </p>
            <p>
              <strong className="font-semibold text-neutral-950">Personal Information Provided by You.</strong> The personal
              information that we collect depends on the context of your interactions with us and the Services, the choices you
              make, and the products and features you use. The personal information we collect may include the following:
            </p>
            <Bullets items={["Names", "Email addresses", "Billing addresses", "Last 4 digits of credit/debit card number"]} />
            <p><strong className="font-semibold text-neutral-950">Sensitive Information.</strong> We do not process sensitive information.</p>
            <p>
              <strong className="font-semibold text-neutral-950">Payment Data.</strong> We may collect data necessary to process
              your payment if you choose to make purchases, such as the last 4 digits of your credit/debit card number. All payment
              data is handled and stored by Stripe. You may find their privacy policy link here: https://stripe.com/au/privacy.
            </p>
            <p>
              All personal information that you provide to us must be true, complete, and accurate, and you must notify us of
              any changes to such personal information.
            </p>
            <Sub>Information automatically collected</Sub>
            <p>
              We automatically collect certain information when you visit, use, or navigate the Services. This information does
              not reveal your specific identity (like your name or contact information) but may include device and usage information,
              such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs,
              device name, country, location, information about how and when you use our Services, and other technical information.
              This information is primarily needed to maintain the security and operation of our Services, and for our internal
              analytics and reporting purposes.
            </p>
            <p>The information we collect includes:</p>
            <Bullets
              items={[
                "Device Data. We collect device data such as information about your computer, phone, tablet, or other device you use to access the Services. Depending on the device used, this device data may include information such as your IP address (or proxy server), device and application identification numbers, location, browser type, hardware model, Internet service provider and/or mobile carrier, operating system, and system configuration information.",
                "Location Data. We collect location data such as information about your device's location, which can be either precise or imprecise. How much information we collect depends on the type and settings of the device you use to access the Services. For example, we may use GPS and other technologies to collect geolocation data that tells us your current location (based on your IP address). You can opt out of allowing us to collect this information either by refusing access to the information or by disabling your Location setting on your device. However, if you choose to opt out, you may not be able to use certain aspects of the Services.",
              ]}
            />
          </Section>

          <Section id="process" num={2} title="How do we process your information?">
            <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
            <Bullets
              items={[
                "To facilitate account creation and authentication and otherwise manage user accounts. We may process your information so you can create and log in to your account, as well as keep your account in working order.",
                "To deliver and facilitate delivery of services to the user. We may process your information to provide you with the requested service.",
                "To respond to user enquiries/offer support to users. We may process your information to respond to your enquiries and solve any potential issues you might have with the requested service.",
                "To send administrative information to you. We may process your information to send you details about our products and services, changes to our terms and policies, and other similar information.",
                "To protect our Services. We may process your information as part of our efforts to keep our Services safe and secure, including fraud monitoring and prevention.",
                "To evaluate and improve our Services, marketing, and your experience. We may process your information when we believe it is necessary to identify usage trends, determine the effectiveness of our promotional campaigns, and to evaluate and improve our Services, products, marketing, and your experience.",
                "To identify usage trends. We may process information about how you use our Services to better understand how they are being used so we can improve them.",
                "To comply with our legal obligations. We may process your information to comply with our legal obligations, respond to legal requests, and exercise, establish, or defend our legal rights.",
              ]}
            />
          </Section>

          <Section id="share" num={3} title="When and with whom do we share your personal information?">
            <Sub>Vendors, Consultants, and Other Third-Party Service Providers</Sub>
            <p>
              We may share your data with third-party vendors, service providers, contractors, or agents ('third parties') who
              perform services for us or on our behalf and require access to such information to do that work.
            </p>
            <p>The third parties we may share personal information with are as follows:</p>
            <Bullets
              items={[
                "AI Service Providers — OpenAI",
                "Invoice and Billing — Stripe",
                "User Account Registration and Authentication — Google Sign-In",
                "Web and Mobile Analytics — Google Analytics",
                "Website Hosting — Wix",
                "Automation/integration services — Make",
              ]}
            />
            <Sub>Client Data Processed on Behalf of Businesses</Sub>
            <p>
              When providing our AI automation and related services to business clients, DISJINX may process personal information
              on behalf of those clients. This information may include information relating to the client's customers, leads,
              employees, suppliers or other contacts.
            </p>
            <p>
              In these circumstances, DISJINX generally processes such information on the client's behalf and in accordance with
              the client's instructions and our agreement with that client. The relevant business client remains responsible for
              determining the purposes for which the information is collected and used and for ensuring that it has the appropriate
              authority to provide the information to DISJINX.
            </p>
            <p>
              Such information may be processed through third-party service providers used to deliver the relevant automation,
              including automation, AI, CRM, email and other software services.
            </p>
            <Sub>Business Transfers</Sub>
            <p>
              We may share or transfer your information in connection with, or during negotiations of, any merger, sale of
              company assets, financing, or acquisition of all or a portion of our business to another company.
            </p>
          </Section>

          <Section id="ai" num={4} title="Do we offer artificial intelligence-based products?">
            <p>
              As part of our Services, we offer products, features, or tools powered by artificial intelligence, machine learning,
              or similar technologies (collectively, 'AI Products'). These tools are designed to enhance your experience and
              provide you with innovative solutions. The terms in this Privacy Policy govern your use of the AI Products within
              our Services.
            </p>
            <Sub>Use of AI Technologies</Sub>
            <p>
              We provide the AI Products through third-party service providers ('AI Service Providers'), including OpenAI. As
              outlined in this Privacy Policy, your input, output, and personal information will be shared with and processed by
              these AI Service Providers to enable your use of our AI Products for purposes outlined in 'WHEN AND WITH WHOM DO WE
              SHARE YOUR PERSONAL INFORMATION?' You must not use the AI Products in any way that violates the terms or policies of
              any AI Service Provider.
            </p>
            <Sub>Our AI Products</Sub>
            <p>Our AI Products are designed for the following functions:</p>
            <Bullets items={["AI automation", "AI bots"]} />
            <Sub>How We Process Your Data Using AI</Sub>
            <p>
              All personal information processed using our AI Products is handled in line with our Privacy Policy and our agreement
              with third parties. This ensures high security and safeguards your personal information throughout the process,
              giving you peace of mind about your data's safety.
            </p>
          </Section>

          <Section id="retention" num={5} title="How long do we keep your information?">
            <p>
              We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy
              Policy, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal
              requirements). Following termination of your account, we generally retain your personal information for up to three
              (3) months before deleting or anonymising it, unless we are required or permitted by law to retain it for longer.
            </p>
            <p>
              When we have no ongoing legitimate business need to process your personal information, we will either delete or
              anonymise such information, or, if this is not possible (for example, because your personal information has been
              stored in backup archives), then we will securely store your personal information and isolate it from any further
              processing until deletion is possible.
            </p>
          </Section>

          <Section id="safe" num={6} title="How do we keep your information safe?">
            <p>
              We have implemented appropriate and reasonable technical and organisational security measures designed to protect
              the security of any personal information we process. However, despite our safeguards and efforts to secure your
              information, no electronic transmission over the Internet or information storage technology can be guaranteed to be
              100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will
              not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will
              do our best to protect your personal information, transmission of personal information to and from our Services is at
              your own risk. You should only access the Services within a secure environment.
            </p>
          </Section>

          <Section id="minors" num={7} title="Do we collect information from minors?">
            <p>
              DISJINX's website and services are intended for individuals aged 18 years or older. We do not knowingly collect
              personal information from individuals under 18 through account registration. If we become aware that we have collected
              personal information from an individual under 18 without appropriate consent, we will take reasonable steps to review
              and, where appropriate, delete that information. If you believe that an individual under 18 has provided personal
              information to us, please contact us at disjinx.support@gmail.com.
            </p>
          </Section>

          <Section id="rights" num={8} title="What are your privacy rights?">
            <p>
              <strong className="font-semibold text-neutral-950">Withdrawing your consent:</strong> If we are relying on your
              consent to process your personal information, which may be express and/or implied consent depending on the applicable
              law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting
              us by using the contact details provided in the section 'HOW CAN YOU CONTACT US ABOUT THIS PRIVACY POLICY?' below.
            </p>
            <p>
              However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when
              applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful
              processing grounds other than consent.
            </p>
            <Sub>Account Information</Sub>
            <p>If you would at any time like to review or change the information in your account or terminate your account, you can:</p>
            <Bullets items={["Contact us using the contact information provided."]} />
            <p>
              Upon your request to terminate your account, we will deactivate or delete your account and information from our active
              databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with
              any investigations, enforce our legal terms and/or comply with applicable legal requirements.
            </p>
            <p>If you have questions or comments about your privacy rights, you may email us at disjinx.support@gmail.com.</p>
          </Section>

          <Section id="dnt" num={9} title="Controls for Do-Not-Track features">
            <p>
              Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ('DNT') feature or
              setting you can activate to signal your privacy preference not to have data about your online browsing activities
              monitored and collected. At this stage, no uniform technology standard for recognising and implementing DNT signals
              has been finalised. As such, we do not currently respond to DNT browser signals or any other mechanism that
              automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we
              must follow in the future, we will inform you about that practice in a revised version of this Privacy Policy.
            </p>
          </Section>

          <Section id="australia" num={10} title="What privacy laws apply in Australia?">
            <p>
              DISJINX operates in Australia and is committed to handling personal information responsibly and transparently. We
              collect, use, disclose and store personal information in accordance with the laws and obligations that apply to our
              business and our information-handling practices.
            </p>
            <p>
              Depending on the circumstances, the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs) may apply to
              DISJINX's handling of personal information. Where the Privacy Act applies, DISJINX will comply with its applicable
              requirements.
            </p>
            <p>
              You may contact DISJINX if you have any questions, concerns or complaints about how we handle your personal
              information. We encourage you to contact us first so that we can investigate and attempt to resolve your concern.
            </p>
            <p>
              If the Privacy Act applies to DISJINX and you are not satisfied with how we have handled your privacy complaint, you
              may be able to make a complaint to the Office of the Australian Information Commissioner (OAIC). Generally, you should
              contact DISJINX first and allow us a reasonable opportunity to respond before contacting the OAIC.
            </p>
            <p>
              Where applicable, you may also have rights to request access to, or correction of, personal information that DISJINX
              holds about you. Information about how to make such a request is provided in the section titled "HOW CAN YOU REVIEW,
              UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?"
            </p>
            <p>
              For privacy enquiries, requests or complaints, please contact us using the contact details provided in the section
              titled "HOW CAN YOU CONTACT US ABOUT THIS PRIVACY POLICY?"
            </p>
          </Section>

          <Section id="updates" num={11} title="Do we make updates to this Privacy Policy?">
            <p>
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated 'Revised'
              date at the top of this Privacy Policy. If we make material changes to this Privacy Policy, we may notify you either
              by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review
              this Privacy Policy frequently to be informed of how we are protecting your information.
            </p>
          </Section>

          <Section id="contact" num={12} title="How can you contact us about this Privacy Policy?">
            <p>
              If you have questions or comments about this policy, you may email us at{" "}
              <a href="mailto:disjinx.support@gmail.com" className="font-semibold text-neutral-950 underline underline-offset-2">
                disjinx.support@gmail.com
              </a>
              .
            </p>
          </Section>

          <Section id="review" num={13} title="How can you review, update, or delete the data we collect from you?">
            <p>
              Based on the applicable laws of your country, you may have the right to request access to the personal information we
              collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information.
              You may also have the right to withdraw your consent to our processing of your personal information. These rights may
              be limited in some circumstances by applicable law. To request to review, update, or delete your personal information,
              please visit: https://disjinx.com/account.
            </p>
          </Section>
        </motion.div>
      </div>
    </main>
  );
}
